/**
 * Enregistrer au micro, honnêtement (TICKET V1.5-10 §24, §25, §26).
 *
 * Ce module ne parle pas au serveur. Il capte, il borne, et il **dit ce qui ne
 * marche pas** — c'est la moitié du travail : un bouton micro qui ne fait rien
 * parce que la permission a été refusée est pire qu'un bouton absent.
 *
 * Les fonctions pures vivent ici pour être testables sans navigateur : le choix
 * du format et la traduction des erreurs sont exactement ce qui diffère d'un
 * navigateur à l'autre, et donc exactement ce qui doit être couvert.
 */

/** Ce qu'on demande à `MediaRecorder`, dans l'ordre de préférence.
 *
 * Chrome et Firefox produisent du WebM/Opus ; Safari — macOS comme iOS — ne
 * connaît que MP4/AAC et rend `false` pour tout le reste. La liste est courte
 * et se termine par la chaîne vide : laisser le navigateur choisir vaut mieux
 * que de refuser d'enregistrer parce qu'aucun nom de la liste ne lui plaît.
 */
export const PREFERRED_MIME_TYPES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4",
  "audio/aac",
  "audio/ogg;codecs=opus",
] as const;

type SupportCheck = (mimeType: string) => boolean;

/** Le premier format que ce navigateur sait produire, ou "" pour son défaut. */
export function preferredMimeType(isSupported?: SupportCheck): string {
  const check =
    isSupported ??
    (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported
      ? (type: string) => MediaRecorder.isTypeSupported(type)
      : null);
  if (!check) return "";
  for (const candidate of PREFERRED_MIME_TYPES) {
    try {
      if (check(candidate)) return candidate;
    } catch {
      // Certains navigateurs lèvent au lieu de rendre `false`. On continue.
    }
  }
  return "";
}

/** Ce navigateur sait-il enregistrer ? */
export function recordingSupported(): boolean {
  return (
    typeof navigator !== "undefined" &&
    typeof navigator.mediaDevices?.getUserMedia === "function" &&
    typeof MediaRecorder !== "undefined"
  );
}

/**
 * L'erreur du navigateur, en une phrase qui dit quoi faire.
 *
 * Les noms viennent de la spécification ; les phrases viennent du chantier.
 * « NotAllowedError » n'apprend rien à quelqu'un qui tient un téléphone dans
 * une main et une perceuse dans l'autre.
 */
export function recorderErrorMessage(error: unknown): string {
  const name = (error as { name?: string })?.name ?? "";
  switch (name) {
    case "NotAllowedError":
    case "SecurityError":
      return "L'accès au micro a été refusé. Autorisez-le dans les réglages du navigateur, puis réessayez.";
    case "NotFoundError":
    case "OverconstrainedError":
      return "Aucun micro n'a été trouvé sur cet appareil. Tapez la question : la réponse est la même.";
    case "NotReadableError":
      return "Le micro est déjà utilisé par une autre application. Fermez-la, puis réessayez.";
    case "AbortError":
      return "L'enregistrement a été interrompu. Réessayez.";
    default:
      return "L'enregistrement n'a pas pu démarrer sur cet appareil. Tapez la question : la réponse est la même.";
  }
}

/** L'extension qui va avec le format produit, pour nommer le fichier envoyé. */
export function extensionFor(mimeType: string): string {
  const base = mimeType.split(";")[0]?.trim().toLowerCase() ?? "";
  if (base === "audio/mp4" || base === "audio/aac") return "m4a";
  if (base === "audio/ogg") return "ogg";
  if (base === "audio/wav" || base === "audio/x-wav") return "wav";
  if (base === "audio/mpeg") return "mp3";
  return "webm";
}

/** « 0:07 ». Une durée d'enregistrement se lit en minutes et secondes. */
export function formatDuration(milliseconds: number): string {
  const total = Math.max(0, Math.floor(milliseconds / 1000));
  const minutes = Math.floor(total / 60);
  return `${minutes}:${String(total % 60).padStart(2, "0")}`;
}

/**
 * La borne de durée, en millisecondes.
 *
 * La même que celle du serveur (`app/domain/speech/limits.py`). Arrêter
 * l'enregistrement ici plutôt que de laisser le serveur le refuser évite de
 * faire monter trois minutes d'audio pour rien.
 */
export const MAX_RECORDING_MS = 180_000;
